import {Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {debounceTime, distinctUntilChanged, Subject, switchMap} from 'rxjs';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {environment} from '../../../environments/environment';
import {faCircleInfo, faMagnifyingGlass} from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'app-generic-search',
    imports: [CommonModule, FormsModule, FontAwesomeModule],
    templateUrl: './search-suggestion.component.html',
    styleUrls: ['./search-suggestion.component.css']
})
export class GenericSearchComponent implements OnInit{
  @Input() apiUrl!: any; // suggestions API
  @Input() searchParamName: string = 'search'; // param key for search
  @Input() method: 'GET' | 'POST' = 'GET'; // HTTP method
  @Input() extraBody: Record<string, any> = {}; // extra fields for POST
  @Input() apiBaseUrl: string | null = null; // override base URL when needed
  @Input() resultsApiUrl!: string; // API for main search results
  @Input() pageSize: number = 10; // default page size
  @Input() minChars: number = 2; // min chars before suggestions
  @Input() resultParamName: string = 'results'; // property in results API response
  @Output() resultsFetched = new EventEmitter<{ searchText: string, results: any[], totalRecords: number,
      totalMonitoringRules?: number,
    nextPage: number | null, previousPage: number | null
  }>();

  @ViewChild('searchWrapper') searchWrapperRef!: ElementRef;

  searchText: string = '';
  suggestions: string[] = [];
  showNoMatchTooltip: boolean = false;
  private noMatchTooltipTimeout: any = null;

  private searchSubject = new Subject<string>();
  private baseUrl = environment.baseUrl;

  private get resolvedBaseUrl(): string {
    return this.apiBaseUrl || this.baseUrl;
  }

  // Pagination
  currentPage: number = 1;
  totalRecords: number = 0;
  nextPage: string | null = null;
  previousPage: string | null = null;

  constructor(
    private http: HttpClient,
    library: FaIconLibrary
  ) {
    library.addIcons(faCircleInfo, faMagnifyingGlass);
    
  }
ngOnInit(): void {
  this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => this.fetchSuggestions(query))
    ).subscribe({
      next: (data: any) => {
        this.suggestions = Array.isArray(data) ? data : data?.search_keys ?? [];
        this.updateNoMatchTooltip();
      },
      error: err => console.error('Error fetching suggestions:', err)
    });
}
  onSearch(query: string) {
    if (query.length >= this.minChars) {
      this.searchSubject.next(query);
      // setTimeout(() => this.updateNoMatchTooltip(), 1000);
    } else {
      this.suggestions = [];
      this.showNoMatchTooltip = false;
    }
  }

  private fetchSuggestions(query: string) {
    if (this.method === 'GET') {
      
      let params = new HttpParams()
      .set(this.searchParamName, query);
      Object.keys(this.extraBody).forEach(key => {
        params = params.set(key, this.extraBody[key]);
      });
      // .set('filter_customers', 'true');
      return  this.http.get<string[]>(`${this.resolvedBaseUrl}${this.apiUrl}?${params.toString()}`)

    } else {
      const body = { ...this.extraBody, [this.searchParamName]: query };
      return this.http.post<any>(`${this.resolvedBaseUrl}${this.apiUrl}`, body);
    }
  }

  updateNoMatchTooltip() {
    const shouldShow = (this.searchText && this.suggestions.length === 0);
    if (shouldShow) {
      this.showNoMatchTooltip = true;
      if (this.noMatchTooltipTimeout) clearTimeout(this.noMatchTooltipTimeout);
      this.noMatchTooltipTimeout = setTimeout(() => {
        this.showNoMatchTooltip = false;
      }, 2000);
    } else {
      this.showNoMatchTooltip = false;
      if (this.noMatchTooltipTimeout) {
        clearTimeout(this.noMatchTooltipTimeout);
        this.noMatchTooltipTimeout = null;
      }
    }
  }

  fetchResults(page: number = 1) {
    this.currentPage = page;
    let parseDate = this.formatPayloadDate(this.searchText)
    
    if (this.method === 'GET') {
      let params = new HttpParams()
        .set('page', page)
        .set('page_size', this.pageSize)
        .set('search', parseDate)

        Object.keys(this.extraBody).forEach(key => {
          params = params.set(key, this.extraBody[key]);
        });
      
      this.http.get<any>(`${this.resolvedBaseUrl}${this.resultsApiUrl}?${params.toString()}`).subscribe(data => {
        this.processResults(data);
      });
    } else {
      let params = new HttpParams()
        .set('page', page)
        .set('page_size', this.pageSize);
      const body = { ...this.extraBody, [this.searchParamName]: parseDate };
      this.http.post<any>(`${this.resolvedBaseUrl}${this.resultsApiUrl}?${params.toString()}`, body).subscribe(data => {
        this.processResults(data);
      });
    }
  }

  private processResults(data: any) {
    this.resultsFetched.emit({
      searchText: this.formatPayloadDate(this.searchText),
      results: data.results,
      // totalRecords: data.count,
      totalRecords: data.count ?? 0,
        totalMonitoringRules: data.total_monitoring_rules ?? undefined,
      nextPage: data.next ?? null,
      previousPage: data.previous ?? null
    });
  }

  onSuggestionClick(suggestion: string) {
    this.searchText = this.formatSuggestionContext(this.splitSuggestion(suggestion).main.trim());
    this.fetchResults(1);
    this.suggestions = [];
    this.showNoMatchTooltip = false;
    if (this.noMatchTooltipTimeout) {
      clearTimeout(this.noMatchTooltipTimeout);
      this.noMatchTooltipTimeout = null;
    }
  }

  splitSuggestion(suggestion: string): { main: string, context: string } {
    const idx = suggestion.indexOf(' in ');
    if (idx !== -1) {
      return {
        main: suggestion.substring(0, idx),
        context: suggestion.substring(idx)
      };
    }
    return { main: suggestion, context: '' };
  }

  formatSuggestionContext(main: string): string {
    const dateMatch = main.match(/\d{4}-\d{2}-\d{2}/);
    if (dateMatch) {
      return dateMatch[0].split('-').reverse().join('-');
    }
    return main;
  }

  formatPayloadDate(main: string): string {
    const dateMatch = main.match(/\d{2}-\d{2}-\d{4}/);
    if (dateMatch) {
      return dateMatch[0].split('-').reverse().join('-');
    }
    return main;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.searchWrapperRef && !this.searchWrapperRef.nativeElement.contains(event.target)) {
      this.suggestions = [];
      this.showNoMatchTooltip = false;
      if (this.noMatchTooltipTimeout) {
        clearTimeout(this.noMatchTooltipTimeout);
        this.noMatchTooltipTimeout = null;
      }
    }
  }
}
